import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEvents, getTasks, getCalendars } from '../api';

const VIEW_TYPES = { DAY: 'Day', WEEK: 'Week', SEVEN_DAYS: '7 Days', MONTH: 'Month' };
const EVENT_STALE_TIME_MS = 60 * 1000;

function getDateRange(currentDate, view, lookbackDays = 0) {
  const timeMin = new Date(currentDate);
  timeMin.setHours(0, 0, 0, 0);
  const timeMax = new Date(currentDate);

  if (view === VIEW_TYPES.MONTH) {
    timeMin.setDate(1);
    timeMax.setMonth(timeMax.getMonth() + 1);
    timeMax.setDate(0);
    timeMax.setHours(23, 59, 59, 999);
  } else if (view === VIEW_TYPES.WEEK) {
    timeMin.setDate(currentDate.getDate() - currentDate.getDay());
    timeMax.setDate(timeMin.getDate() + 7);
    timeMax.setHours(23, 59, 59, 999);
  } else if (view === VIEW_TYPES.SEVEN_DAYS) {
    timeMax.setDate(timeMax.getDate() + 7);
    timeMax.setHours(23, 59, 59, 999);
  } else {
    timeMax.setDate(timeMax.getDate() + 1);
    timeMax.setHours(23, 59, 59, 999);
  }

  // Extend timeMin into the past if lookback requested
  if (lookbackDays > 0) {
    const lookback = new Date(timeMin);
    lookback.setDate(lookback.getDate() - lookbackDays);
    return { timeMin: lookback.toISOString(), timeMax: timeMax.toISOString() };
  }

  return { timeMin: timeMin.toISOString(), timeMax: timeMax.toISOString() };
}

function dateKey(currentDate, view, lookbackDays = 0) {
  const { timeMin, timeMax } = getDateRange(currentDate, view, lookbackDays);
  return `${timeMin}_${timeMax}`;
}

function getAdjacentDate(currentDate, view, direction) {
  const next = new Date(currentDate);

  if (view === VIEW_TYPES.MONTH) {
    next.setMonth(currentDate.getMonth() + direction);
  } else if (view === VIEW_TYPES.WEEK || view === VIEW_TYPES.SEVEN_DAYS) {
    next.setDate(currentDate.getDate() + (direction * 7));
  } else {
    next.setDate(currentDate.getDate() + direction);
  }

  return next;
}

function prefetchEventsForDate(queryClient, date, view, lookbackDays) {
  const range = getDateRange(date, view, lookbackDays);

  return queryClient.prefetchQuery({
    queryKey: ['events', dateKey(date, view, lookbackDays)],
    queryFn: () => getEvents(range.timeMin, range.timeMax),
    staleTime: EVENT_STALE_TIME_MS,
  });
}

export function useEvents(currentDate, view, lookbackDays = 0, enabled = true) {
  const range = getDateRange(currentDate, view, lookbackDays);

  return useQuery({
    queryKey: ['events', dateKey(currentDate, view, lookbackDays)],
    queryFn: () => getEvents(range.timeMin, range.timeMax),
    placeholderData: (prev) => prev,
    enabled,
    staleTime: EVENT_STALE_TIME_MS,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

export function usePrefetchAdjacentEvents(currentDate, view, lookbackDays = 0, enabled = true) {
  const queryClient = useQueryClient();
  const currentDateTime = currentDate?.getTime?.() || 0;

  useEffect(() => {
    if (!enabled || !currentDateTime) return undefined;

    const timeoutId = window.setTimeout(() => {
      [-1, 1].forEach(direction => {
        const adjacentDate = getAdjacentDate(currentDate, view, direction);
        prefetchEventsForDate(queryClient, adjacentDate, view, lookbackDays).catch(() => {});
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [currentDate, currentDateTime, enabled, lookbackDays, queryClient, view]);
}

export function useTasks(enabled = true) {
  return useQuery({
    queryKey: ['tasks'],
    queryFn: getTasks,
    placeholderData: (prev) => prev,
    enabled,
    staleTime: 2 * 60 * 1000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

export function useCalendars(enabled = true) {
  return useQuery({
    queryKey: ['calendars'],
    queryFn: getCalendars,
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
    enabled,
  });
}

export function useInvalidateEvents() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['events'] });
}

export function useInvalidateTasks() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['tasks'] });
}

export function useSetTasksData() {
  const queryClient = useQueryClient();
  return (updater) => queryClient.setQueryData(['tasks'], updater);
}

export function useSetEventsData() {
  const queryClient = useQueryClient();
  return (updater) => {
    // Update all cached event queries (different date ranges)
    queryClient.setQueriesData({ queryKey: ['events'] }, (old) => {
      if (!old) return old;
      return updater(old);
    });
  };
}
